import { InfoCircleOutlined } from '@ant-design/icons';
import { Form, Input, InputNumber, Popover, Spin, Switch } from 'antd';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { firebaseFunctions } from '../../../firebase/firebase';
import { FirebaseCallableFunctions } from '../../../firebase/firebase.models';
import { Offer, OfferCompanyDetails } from '../../../models/offers.model';

interface Props {
  offer: Offer;
  onFormReady: (values: OfferCompanyDetails | null) => void;
}

export const TenantCompanyStep: React.FC<Props> = ({ offer, onFormReady }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [noKRS, setNoKRS] = useState(false);
  const [regonLoading, setRegonLoading] = useState(false);

  useEffect(() => {
    if (offer.tenantCompanyDetails?.nip) {
      form.setFieldsValue(offer.tenantCompanyDetails);
      form
        .validateFields()
        .then(() => onFormReady(form.getFieldsValue()))
        .catch(() => onFormReady(null));
    }
  }, []);

  const fetchRegon = async (nip: string) => {
    setRegonLoading(true);
    try {
      const callRegonAPI = firebaseFunctions.httpsCallable(FirebaseCallableFunctions.ON_CALL_REGON_API);
      const res = await callRegonAPI({ NIP: nip });
      if (res.data.status !== 'ERROR' && res.data.resp?.ErrorCode !== '4') {
        const resp = res.data.resp;
        form.setFieldsValue({
          nazwa: resp.Nazwa[0],
          regon: resp.Regon[0],
          kraj: 'POLSKA',
          kodPocztowy: resp.KodPocztowy[0],
          miejscowosc: resp.Miejscowosc[0].toUpperCase(),
          nrDomu: `${resp.NrNieruchomosci[0]}${resp.NrLokalu[0] ? ' / ' + resp.NrLokalu[0] : ''}`,
          ulica: resp.Ulica[0].toUpperCase(),
        });
      }
    } catch (e) {
      console.error('REGON API error:', e);
    } finally {
      setRegonLoading(false);
      form
        .validateFields()
        .then(() => onFormReady(form.getFieldsValue()))
        .catch(() => onFormReady(null));
    }
  };

  const notifyIfValid = () => {
    form
      .validateFields()
      .then(() => onFormReady(form.getFieldsValue()))
      .catch(() => onFormReady(null));
  };

  const handleValuesChange = (changedValues: Record<string, unknown>) => {
    if ('nip' in changedValues) {
      const digits = String(changedValues.nip ?? '').replace(/\D/g, '');
      if (digits.length === 10) {
        fetchRegon(digits);
      } else {
        onFormReady(null);
      }
      return;
    }
    notifyIfValid();
  };

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={offer.tenantCompanyDetails ?? {}}
      onValuesChange={handleValuesChange}
      style={{ maxWidth: 520 }}>
      <Form.Item
        label={t('FORMS.CONNECT_COMPANY.NIP')}
        name="nip"
        rules={[{ required: true, message: t('VALIDATION.FIELD_REQUIRED') }]}>
        <InputNumber
          controls={false}
          style={{ width: '100%' }}
          disabled={regonLoading}
          suffix={regonLoading ? <Spin size="small" /> : undefined}
        />
      </Form.Item>

      <Form.Item
        label={t('FORMS.CONNECT_COMPANY.COMPANY_NAME')}
        name="nazwa"
        rules={[{ required: true, message: t('VALIDATION.FIELD_REQUIRED') }]}>
        <Input />
      </Form.Item>

      <div className="flex items-center gap-2 mb-4">
        <Switch
          checked={noKRS}
          onChange={(checked) => {
            setNoKRS(checked);
            if (checked) form.setFieldValue('numerKRS', '');
            notifyIfValid();
          }}
          size="small"
        />
        <span className="text-sm">{t('VALIDATION.NO_KRS')}</span>
        <Popover
          trigger="hover"
          content={
            <div className="font-semibold w-96">
              {t('KRS_POPOVER_1')}
              <br />
              <br />
              {t('KRS_POPOVER_2')}
            </div>
          }>
          <InfoCircleOutlined className="ml-1" />
        </Popover>
      </div>

      {!noKRS && (
        <Form.Item
          label={t('FORMS.CONNECT_COMPANY.KRS')}
          name="numerKRS"
          rules={[{ required: true, message: t('VALIDATION.KRS_REQUIRED') }]}>
          <Input />
        </Form.Item>
      )}

      <Form.Item label="REGON" name="regon">
        <Input />
      </Form.Item>
      <Form.Item label={t('FORMS.CONNECT_COMPANY.COUNTRY')} name="kraj">
        <Input />
      </Form.Item>
      <Form.Item label={t('FORMS.CONNECT_COMPANY.POSTAL_CODE')} name="kodPocztowy">
        <Input />
      </Form.Item>
      <Form.Item
        label={t('FORMS.CONNECT_COMPANY.CITY')}
        name="miejscowosc"
        rules={[{ required: true, message: t('VALIDATION.FIELD_REQUIRED') }]}>
        <Input />
      </Form.Item>
      <Form.Item label={t('FORMS.CONNECT_COMPANY.STREET')} name="ulica">
        <Input />
      </Form.Item>
      <Form.Item label={t('FORMS.CONNECT_COMPANY.HOUSE_NUMBER')} name="nrDomu">
        <Input />
      </Form.Item>
    </Form>
  );
};
